// src/components/UserData.tsx
// ============================================================================
// FILE ROLE: Logged-in user controller (the "app shell" after authentication).
// - Bootstraps Cognito user → AppSync UserRecord (get/create)
// - Maintains vault-backed lists: contactPointsVaultEnc + crushListVaultEnc
// - Runs onboarding gates: phone verification → tier choice → upgrade(age+payment)
// - Coordinates matches, settings, membership upgrades, and push notifications
//
// TRUST BOUNDARY: authed user UI.
// - Never trust client-supplied IDs/claims for authz; backend MUST enforce ownership.
// - Treat all user inputs (contact points, crush targets, social handles) as untrusted.
// - Do not log tokens or other secrets (Authorization, idToken/accessToken, FCM token).
//
// TODO tag convention (docs/internal/protocols.md):
// TODO(SEC|REL|TEST|CLEANUP): <what> — <why/risk> — <suggested fix>
// ============================================================================

import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  signOut,
} from "aws-amplify/auth";
import { generateClient, GraphQLResult } from "aws-amplify/api";

import { getToken } from "firebase/messaging";
import { messaging } from "../firebase";

import ContactPointsPanel, { CPItem } from "./ContactPointsPanel";
import CrushList, { type CrushListHandle } from "./CrushList";
import MatchesList from "./MatchesList";
import ProfileSettings from "./ProfileSettings";
import MatchPopup from "./MatchPopup";
import ChangePrimaryEmail from "./ChangePrimaryEmail";
import ChangePrimaryPhone from "./ChangePrimaryPhone";

import PhoneVerificationGate from "./PhoneVerificationGate";
import AgeVerificationGate from "./AgeVerificationGate";
import PaymentGate from "./PaymentGate";
import DeleteAccountModal from "./DeleteAccountModal";
import ScheduledDeletionScreen from "./ScheduledDeletionScreen";

import PushNotificationWelcomeModal from "./PushNotificationWelcomeModal";
import { SecretAdmirerBadge } from "./SecretAdmirerBadge";

import { ZodiacSign } from "@/utils/zodiac";
import { replayPresignupList } from "../utils/replayPresignupList";
import { replayPresignupHandles } from "../utils/replayPresignupHandles";
import { ZodiyakHelper } from "./ZodiyakHelper";
import { TipSection } from "@/utils/zodiyakTips";
import { asset } from "@/lib/assets";

//import { getFcmToken } from "../pushNotification";
import { DM_LINKS } from "../config/dmLinks";
import MemeSubmissionModal from './MemeSubmissionModal';
import ZodiacPhotoFrame from './ZodiacPhotoFrame';
import WaitWithHopeModal from './WaitWithHopeModal';
import type { Schema } from '../../amplify/data/resource';
import toast from 'react-hot-toast';


/* ============================================================================ *
 * TRUST BOUNDARY: AppSync client
 * - Authed user context; backend auth/authz must be enforced server-side.
 * - Typed client provides full TypeScript intellisense for models & operations
 * ========================================================================== */
const client = generateClient<Schema>();

/* ============================================================================ *
 * Types
 * ========================================================================== */

type StageType = "signin" | "signup" | "welcome" | "forgot-password";

interface UserDataProps {
  setStage: React.Dispatch<React.SetStateAction<StageType>>;
}

type Interest = "1" | "2" | "3";

interface Crush {
  contact: string;
  contactHash?: string;
  interestLevel: Interest;
  addedAt: string;
  contactType?: string;
  status?: "pending" | "confirmed";
  needsReconfirm?: boolean;  // ✅ ADD THIS
}

/*async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  const bytes = Array.from(new Uint8Array(buf));
  return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
}*/

type JsonString = string;
type AWSDateTimeLike = string | number | null;

export interface UserRecord {
  id: string;
  email?: string | null;
  phone?: string | null;
  termsAccepted?: boolean | null;
  accountActive: boolean;
  pushNotifications: boolean;
  cookiePolicyChoice?: string | null;
  pushNotificationReminderDismissed?: boolean | null;
  phoneVerified?: boolean | null;
  ageVerified?: boolean | null;
  zodiacSign?: string | null;
  zipcode?: string | null;
  verificationAttempts?: number | null;
  lastVerificationAttempt?: AWSDateTimeLike | null;
  verificationCooldownUntil?: AWSDateTimeLike | null;
  tier?: string | null; // "free" | "paid"
  hasSeenTierChoice?: boolean | null;
  pricePaidCents?: number | null;
  promoCodeUsed?: string | null;
  planExpiresAt?: AWSDateTimeLike | null;
  contactPointsVaultEnc?: JsonString | null;
  crushListVaultEnc?: JsonString | null;
  vaultKeyId?: string | null;
  totalContactPoints?: number | null;
  totalCrushesAdded?: number | null;
  totalMatchesMade?: number | null;
  totalMatchesSeen?: number | null;
  hashedZip?: string | null;
  lastActiveDate?: AWSDateTimeLike | null;
  referralCode?: string | null;
  referredByUserId?: string | null;
  createdAt?: AWSDateTimeLike | null;
  updatedAt?: AWSDateTimeLike | null;
  deletedAt?: AWSDateTimeLike | null;
}

interface GetUserResp {
  getUser: UserRecord | null;
}
interface CreateUserResp {
  createUser: UserRecord;
}
interface UpdateUserResp {
  updateUser: UserRecord;
}

/* ============================================================================ *
 * Helpers
 * ========================================================================== */

function digitsOnly(s: string): string {
  return String(s ?? "").replace(/\D/g, "");
}

function formatUSPhone(digits: string): string {
  const n = digitsOnly(digits).slice(0, 10);
  if (n.length < 4) return n;
  if (n.length < 7) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}

function displayPhone(phone?: string | null): string {
  if (!phone) return "";
  const digits = digitsOnly(phone);
  if (digits.length === 11 && digits.startsWith("1")) return formatUSPhone(digits.slice(1));
  if (digits.length === 10) return formatUSPhone(digits);
  return String(phone);
}

function readJson<T = any>(v: unknown, fallback: T): T {
  if (v == null) return fallback;
  if (typeof v === "string") {
    try {
      return JSON.parse(v) as T;
    } catch {
      return fallback;
    }
  }
  if (typeof v === "object") return v as T;
  return fallback;
}

function toSocialProfileUrl(kind: string, value: string): string {
  const v = (value || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;

  const handle = v.startsWith("@") ? v.slice(1) : v;

  switch (kind) {
    case "instagram":
      return `https://instagram.com/${handle}`;
    case "tiktok":
      return `https://www.tiktok.com/@${handle}`;
    case "snapchat":
      return `https://snapchat.com/add/${handle}`;
    case "facebook":
      return `https://facebook.com/${handle}`;
    case "linkedin":
      return `https://www.linkedin.com/in/${handle}`;
    default:
      return "";
  }
}

function writeJson(v: unknown): string {
  return typeof v === "string" ? v : JSON.stringify(v ?? null);
}

const normalizePhone = (s: string) => {
  const d = (s || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (d.length === 10) return `+1${d}`;
  return d ? `+${d}` : "";
};

const normalizeEmail = (s: string) => (s || "").trim().toLowerCase();

function getCPList(user: UserRecord | null): CPItem[] {
  const obj = readJson<{ items?: CPItem[] }>(user?.contactPointsVaultEnc, {});
  return Array.isArray(obj.items) ? obj.items : [];
}

function getCrushList(user: UserRecord | null): Crush[] {
  const obj = readJson<{ items?: Crush[] }>(user?.crushListVaultEnc, {});
  return Array.isArray(obj.items) ? obj.items : [];
}

const toAwsJson = (obj: any) => JSON.stringify(obj ?? {});

function assertNoErrors<T>(res: GraphQLResult<T>, where: string) {
  if (res.errors?.length) {
    const msg = res.errors[0].message || "Unknown AppSync error";
    throw new Error(`${where}: ${msg}`);
  }
}

/* ============================================================================ *
 * GraphQL wrappers
 * ========================================================================== */

export async function gqlCreateUser(id: string, _email: string, phone: string) {
  // NOTE: `email` parameter is accepted for caller-signature stability but is
  // no longer written through AppSync — User.email is encrypted server-side
  // by the updateUserEmail Lambda, called separately after gqlCreateUser.
  const input = {
    id,
    phone,
    termsAccepted: true,
    accountActive: true,
    pushNotifications: false,
    cookiePolicyChoice: "strict",
    tier: "free",
    hasSeenTierChoice: false,
  };

  const res = (await client.graphql({
    query: /* GraphQL */ `
      mutation CreateUser($input: CreateUserInput!) {
        createUser(input: $input) {
          id
          pushNotifications
          accountActive
          cookiePolicyChoice
          phoneVerified
          ageVerified
          zodiacSign
          zipcode
          verificationAttempts
          lastVerificationAttempt
          verificationCooldownUntil
          tier
          hasSeenTierChoice
          pricePaidCents
          promoCodeUsed
          planExpiresAt
          vaultKeyId
          createdAt
          updatedAt
        }
      }
    `,
    variables: { input },
  })) as GraphQLResult<CreateUserResp>;

  assertNoErrors(res, "createUser");

  const created = res.data?.createUser ?? null;

  // Note: PrimaryEmailLookup upsert moved out of gqlCreateUser. The bootstrap
  // useEffect calls upsertEmailLookup after fetchUserPii populates u.email.

  return created;
}

export async function gqlGetUser(id: string) {
  const res = (await client.graphql({
    query: /* GraphQL */ `
      query GetUser($id: ID!) {
        getUser(id: $id) {
          id
          pushNotifications
          accountActive
          cookiePolicyChoice
          phoneVerified
          ageVerified
          zodiacSign
          zipcode
          verificationAttempts
          lastVerificationAttempt
          verificationCooldownUntil
          tier
          hasSeenTierChoice
          pricePaidCents
          promoCodeUsed
          planExpiresAt
          vaultKeyId
          totalCrushesAdded
          totalMatchesMade
          totalMatchesSeen
          createdAt
          updatedAt
          deletedAt
        }
      }
    `,
    variables: { id },
  })) as GraphQLResult<GetUserResp>;

  assertNoErrors(res, "getUser");
  return res.data?.getUser ?? null;
}

export async function gqlUpdateUser(input: Partial<UserRecord> & { id: string }) {
  const normalizedInput: any = { ...input };

  if (input.contactPointsVaultEnc && typeof input.contactPointsVaultEnc !== "string") {
    normalizedInput.contactPointsVaultEnc = toAwsJson(input.contactPointsVaultEnc);
  }
  if (input.crushListVaultEnc && typeof input.crushListVaultEnc !== "string") {
    normalizedInput.crushListVaultEnc = toAwsJson(input.crushListVaultEnc);
  }

  const res = (await client.graphql({
    query: /* GraphQL */ `
      mutation UpdateUser($input: UpdateUserInput!) {
        updateUser(input: $input) {
          id
          pushNotifications
          accountActive
          cookiePolicyChoice
          phoneVerified
          ageVerified
          zodiacSign
          zipcode
          verificationAttempts
          lastVerificationAttempt
          verificationCooldownUntil
          tier
          hasSeenTierChoice
          pricePaidCents
          promoCodeUsed
          planExpiresAt
          vaultKeyId
          totalCrushesAdded
          totalMatchesMade
          totalMatchesSeen
          createdAt
          updatedAt
          deletedAt
        }
      }
    `,
    variables: { input: normalizedInput },
  })) as GraphQLResult<UpdateUserResp>;

  assertNoErrors(res, "updateUser");
  return res.data?.updateUser ?? null;
}

/* ============================================================================ *
 * PII REST helper
 * - email, phone, and vault fields are no longer returned via AppSync selection
 *   sets (they went through this path as plaintext previously). Instead, we
 *   call the getUserPii REST Lambda once after bootstrap; it authenticates the
 *   caller via Cognito, decrypts the stored envelopes server-side, and returns
 *   plaintext JSON. Never cache the result — always fetch fresh per session.
 * ========================================================================== */

async function fetchUserPii(apiBase: string, token: string): Promise<{
  email: string;
  phone: string;
  contactPointsVault: any[];
  crushListVault: any[];
} | null> {
  try {
    const res = await fetch(`${apiBase}get-user-pii`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.ok) return null;
    return {
      email: data.email ?? "",
      phone: data.phone ?? "",
      contactPointsVault: data.contactPointsVault ?? [],
      crushListVault: data.crushListVault ?? [],
    };
  } catch {
    return null;
  }
}

/**
 * Re-fetch decrypted PII via getUserPii and merge into user state.
 * Call after user-triggered actions (add crush, verify phone, age verify,
 * confirm handle) so the UI doesn't go stale until next bootstrap.
 * Errors are non-fatal — call sites should swallow failures.
 */
export async function refreshUserPii(
  apiBase: string,
  token: string,
  setUser: (updater: (prev: any) => any) => void
): Promise<void> {
  const pii = await fetchUserPii(apiBase, token);
  if (!pii) return;
  setUser(prev => prev ? {
    ...prev,
    email: pii.email || prev.email,
    phone: pii.phone || prev.phone,
    contactPointsVaultEnc: JSON.stringify({ items: pii.contactPointsVault ?? [] }),
    crushListVaultEnc: JSON.stringify({ items: pii.crushListVault ?? [] }),
  } : prev);
}

/* ============================================================================ *
 * Email lookup REST helper
 * - Replaces the deleted gqlUpsertPrimaryEmailLookup. The new Lambda hashes
 *   the email server-side with the contact pepper and writes the
 *   { emailHash → userId } mapping. Best-effort — failures are swallowed.
 * ========================================================================== */

async function upsertEmailLookup(apiBase: string, token: string, email: string): Promise<void> {
  try {
    await fetch(`${apiBase}upsert-email-lookup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({ email }),
    });
  } catch {
    // non-fatal — lookup table is best-effort
  }
}

/* ============================================================================ *
 * Encrypted email writer
 * - Writes the user's primary email through a backend Lambda that normalizes
 *   and encrypts the value before storing it in DynamoDB. Replaces the prior
 *   AppSync writes of User.email (gqlCreateUser input + handleEmailChange).
 * ========================================================================== */

async function updateUserEmail(apiBase: string, token: string, email: string): Promise<void> {
  try {
    await fetch(`${apiBase}update-user-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({ email }),
    });
  } catch {
    // non-fatal
  }
}

/* ============================================================================ *
 * Encrypted vault writers
 * - Both helpers send plaintext items[]; the Lambda encrypts PII fields per
 *   item server-side and writes the resulting blob to DynamoDB. Replaces the
 *   prior AppSync writes of contactPointsVaultEnc / crushListVaultEnc.
 * - Exported so PaymentGate (and any future caller) can reuse without
 *   re-declaring the fetch shape.
 * ========================================================================== */

export async function updateContactPointsVault(apiBase: string, token: string, items: any[]): Promise<void> {
  try {
    await fetch(`${apiBase}update-contact-points-vault`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({ items }),
    });
  } catch {
    // non-fatal
  }
}

export async function updateCrushListVault(apiBase: string, token: string, items: any[]): Promise<void> {
  try {
    await fetch(`${apiBase}update-crush-list-vault`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({ items }),
    });
  } catch {
    // non-fatal
  }
}

/* ============================================================================ *
 * Brand presentation tokens (UI only — no logic depends on these)
 * Mirrors the inline-style brand language used in ZodiacMemeLibrary.tsx
 * and the landing-page sign-in / sign-up cards.
 * ========================================================================== */

const BRAND_INK = "#1A1A1A";
const BRAND_INK_MID = "#555";
const BRAND_CREAM = "#F0DFC8";
const BRAND_CORAL = "#E05A3A";
const BRAND_FONT_DISPLAY = "'Fredoka', sans-serif";
const BRAND_FONT_BODY = "'Nunito', sans-serif";

const sectionCardStyle: React.CSSProperties = {
  background: "#FFFFFF",
  border: `3px solid ${BRAND_INK}`,
  borderRadius: 20,
  boxShadow: `6px 6px 0px ${BRAND_INK}`,
  marginBottom: 16,
  overflow: "hidden",
};

function sectionHeaderStyle(expanded: boolean): React.CSSProperties {
  return {
    fontFamily: BRAND_FONT_DISPLAY,
    fontSize: 20,
    fontWeight: 700,
    color: BRAND_CORAL,
    padding: "16px 20px",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderBottom: expanded ? "2px solid rgba(26,26,26,0.1)" : "none",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    outline: "none",
    boxShadow: "none",
  };
}

const sectionBodyStyle: React.CSSProperties = {
  padding: "16px 20px",
};

const fieldLabelStyle: React.CSSProperties = {
  fontFamily: BRAND_FONT_DISPLAY,
  fontSize: 15,
  fontWeight: 600,
  color: BRAND_INK,
  margin: 0,
};

const fieldValueStyle: React.CSSProperties = {
  fontFamily: BRAND_FONT_BODY,
  fontSize: 14,
  color: BRAND_INK_MID,
  margin: 0,
  marginTop: 2,
  wordBreak: "break-all",
};

const actionPillStyle: React.CSSProperties = {
  background: BRAND_CORAL,
  color: "#FFFFFF",
  border: `3px solid ${BRAND_INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${BRAND_INK}`,
  padding: "8px 20px",
  fontFamily: BRAND_FONT_DISPLAY,
  fontSize: 15,
  fontWeight: 600,
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "all 0.1s ease",
};

const footerPillCoralStyle: React.CSSProperties = {
  background: BRAND_CORAL,
  color: "#FFFFFF",
  border: `3px solid ${BRAND_INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${BRAND_INK}`,
  padding: "10px 24px",
  fontFamily: BRAND_FONT_DISPLAY,
  fontSize: 15,
  fontWeight: 700,
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "all 0.1s ease",
};

const footerPillGhostStyle: React.CSSProperties = {
  background: "#FFFFFF",
  color: BRAND_INK,
  border: `3px solid ${BRAND_INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${BRAND_INK}`,
  padding: "10px 24px",
  fontFamily: BRAND_FONT_DISPLAY,
  fontSize: 15,
  fontWeight: 700,
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "all 0.1s ease",
};

const dividerRowStyle: React.CSSProperties = {
  borderTop: "1px solid rgba(26,26,26,0.1)",
  paddingTop: 16,
  marginTop: 16,
};

/* ============================================================================ *
 * Component
 * ========================================================================== */

export default function UserData({ setStage }: UserDataProps) {
  const [cogUser, setCogUser] = useState<FetchUserAttributesOutput | null>(null);
  const [user, setUser] = useState<UserRecord | null>(null);

  const [loading, setLoading] = useState(false);

  // User-visible error message rendered in the initial-load fallback when
  // the user fetch hangs past the 10s timeout below. Cleared automatically
  // on successful user load (the !user fallback unmounts).
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [showPushWelcome, setShowPushWelcome] = useState(false);

  const [pushNotifications, setPushNotifications] = useState(false);
  const [accountActive, setAccountActive] = useState(true);
  const [cookiePolicyChoice, setCookiePolicyChoice] = useState("strict");

  const [isPushNotificationOpen, setisPushNotificationOpen] = useState(false);
  const [isAccountActivationOpen, setisAccountActivationOpen] = useState(false);
  const [isCookiesOpen, setCookiesOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [upgradeStage, setUpgradeStage] = useState<"none" | "plan">("none");
  const handleStartUpgrade = () => setUpgradeStage("plan");

  const [membershipExpanded, setMembershipExpanded] = useState(false);

  const [matchesRefreshKey, setMatchesRefreshKey] = useState(0);
  const [showMatchPopup, setShowMatchPopup] = useState(false);
  const showMatchPopupRef = useRef(false);

  const claimedPrimaryOnce = useRef(false);

  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [showChangePhone, setShowChangePhone] = useState(false);
  const [expandedSection, setExpandedSection] = useState<"settings" | "crushlist" | "crushids" | "matches" | null>(
    null
  );
  const [reconfirmedCrushes, setReconfirmedCrushes] = useState<Set<string>>(new Set());
  const [zodiyakEnabled] = useState(true);
  const [pillHov,   setPillHov]   = useState<string | null>(null);
  const [pillPress, setPillPress] = useState<string | null>(null);
  const [currentTipSection, setCurrentTipSection] = useState<TipSection>("welcome");

//  const [hasSecretAdmirer, setHasSecretAdmirer] = useState(false);
  const [canSubmitToday, setCanSubmitToday] = useState(false);
  const [showMemeModal, setShowMemeModal] = useState(false);
  const [showPhotoFrame, setShowPhotoFrame] = useState(false);
  const [admirerCount, setAdmirerCount] = useState<number | null>(null);

  const crushListSectionRef = useRef<HTMLDivElement | null>(null);
  const crushIdsSectionRef = useRef<HTMLDivElement | null>(null);
  const settingsSectionRef = useRef<HTMLDivElement | null>(null);
  const crushListRef = useRef<CrushListHandle>(null);
  const [showNoMatchModal, setShowNoMatchModal] = useState(false);

  const crushes = useMemo(() => getCrushList(user), [user?.crushListVaultEnc]);
  const confirmedCrushes = useMemo(
    () => crushes.filter((c) => c.status === "confirmed" || !c.status),
    [crushes]
  );
  const cpItems = useMemo(() => getCPList(user), [user?.contactPointsVaultEnc]);

  const isPaid = user?.tier === "paid";

  const CRUSH_CAP = 12;
  const usedCrushes = confirmedCrushes.length;
  const leftCrushes = Math.max(0, CRUSH_CAP - usedCrushes);

  const crushCap = CRUSH_CAP;
  const usedCreditsDisplay = usedCrushes;

  
  

  const userVaultData = useMemo(() => {
    try {
      const vault = user?.contactPointsVaultEnc;
      if (!vault) return [];
      const parsed = typeof vault === 'string' ? JSON.parse(vault) : vault;
      const arr = Array.isArray(parsed) ? parsed : (parsed?.items || []);
      return arr.map((item: any) => ({
        kind: item.kind || 'email',
        platform: item.platform,
        value: item.value || item.contact || '',
      }));
    } catch (err) {
      void ['[UserData] Failed to parse vault:', err];
      return [];
    }
  }, [user?.contactPointsVaultEnc]);



  useEffect(() => {
    if (user?.tier && user.tier !== "free") {
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        /* ignore */
      }
    }
  }, [user?.tier]);
      useEffect(() => {
      checkDailySubmission();
    }, []);


const checkDailySubmission = async () => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const session = await fetchAuthSession({ forceRefresh: true });
    const userId = session.tokens?.idToken?.payload.sub as string;
    
    // Check if THIS user has already submitted today
    const result = await client.graphql({
      query: `
        query CheckUserSubmission($userId: String!, $submissionDate: String!) {
          getMemeSubmission(userId: $userId, submissionDate: $submissionDate) {
            userId
            submissionDate
          }
        }
      `,
      variables: {
        userId,
        submissionDate: today
      }
    }) as any;
    
    // If record exists, user already submitted today
    const hasSubmitted = result.data?.getMemeSubmission !== null;
    setCanSubmitToday(!hasSubmitted);
  } catch (error) {
    // If error (like record not found), allow submission
    void ['Error checking daily submission:', error];
    setCanSubmitToday(true);
  }
};

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ----------------------------------------------------------------------
  // Initial-load timeout — if the user record hasn't arrived after 10s the
  // accessToken is most likely revoked / stale. Force a clean signOut and
  // bounce back to the signin form so the next attempt requests a fresh
  // token instead of looping on the dead one.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (user) {
      if (errorMessage) setErrorMessage("");
      return;
    }
    const timeout = setTimeout(() => {
      if (!user) {
        setLoading(false);
        signOut()
          .then(() => setStage("signin"))
          .catch(() => setStage("signin"));
      }
    }, 20000); // Increased from 10s — slow mobile connections need more time for cold Lambda + DDB read
    return () => clearTimeout(timeout);
  }, [user, errorMessage, setStage]);

  useEffect(() => {
    if (!user) return;

    const items = getCPList(user);
    const normEmail = (user.email ?? "").trim().toLowerCase();

    const normPhone = (() => {
      const d = (user.phone ?? "").replace(/\D/g, "");
      if (!d) return "";
      if (d.length === 11 && d.startsWith("1")) return `+${d}`;
      if (d.length === 10) return `+1${d}`;
      return user.phone ?? "";
    })();

    const needsEmail =
      !!normEmail &&
      !items.some((i) => i.kind === "email" && (i.value || "").toLowerCase() === normEmail);

    const needsPhone = !!normPhone && !items.some((i) => i.kind === "phone" && (i.value || "") === normPhone);

    if (!needsEmail && !needsPhone) return;

    (async () => {
      const next = [...items];
      const now = new Date().toISOString();

      if (needsEmail) {
        next.push({ kind: "email", value: normEmail, status: "verified", addedAt: now } as any);
      }
      if (needsPhone) {
        next.push({ kind: "phone", value: normPhone, status: "verified", addedAt: now } as any);
      }

      // Vault write goes through the encrypted REST Lambda; the items array
      // is sent plaintext and encrypted server-side per item.
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          const rawBase = import.meta.env.VITE_API_BASE_URL as string;
          const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
          await updateContactPointsVault(apiBase, `Bearer ${idToken}`, next);
        }
      } catch {
        // non-fatal — auto-attach is retried on next session
      }

      // Reflect new vault locally so the cpItems memo re-runs without refetch.
      setUser(prev => prev ? {
        ...prev,
        contactPointsVaultEnc: JSON.stringify({ items: next }),
      } : prev);
    })();
  }, [user?.phone, user?.email]);

useEffect(() => {
  (async () => {
    try {
      
      const attrs = await fetchUserAttributes();
      
      setCogUser(attrs);
      setStage("welcome");
    } catch (e) {
      console.error("[UserData] fetchUserAttributes FAILED:", e);
    }
  })();
}, [setStage]);

  useEffect(() => {
    if (!user) return;

    const shouldShowReminder =
      !user.pushNotifications &&
      !user.pushNotificationReminderDismissed &&
      (user.totalCrushesAdded ?? 0) > 0 &&
      !firstCrushPushTriggered.current;

    if (shouldShowReminder) {
      pushWelcomeTimerRef.current = setTimeout(() => {
        if (!showMatchPopupRef.current) setShowPushWelcome(true);
      }, 1000);
      return () => {
        if (pushWelcomeTimerRef.current) clearTimeout(pushWelcomeTimerRef.current);
      };
    }
  }, [user?.id, user?.pushNotifications, user?.pushNotificationReminderDismissed, user?.totalCrushesAdded]);

  // Brief W: surface the notification modal again the moment the user adds
  // their first crush — fires regardless of whether they dismissed the cold-
  // login prompt. Once-per-session guarded by ref. Watches totalCrushesAdded
  // transition from 0 to >0.
  const firstCrushPushTriggered = useRef(false);
  const prevTotalCrushesAdded = useRef<number>(0);
  const pushWelcomeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!user) return;
    const prev = prevTotalCrushesAdded.current;
    const next = user.totalCrushesAdded ?? 0;
    prevTotalCrushesAdded.current = next;

    if (firstCrushPushTriggered.current) return;
    if (prev === 0 && next > 0 && !user.pushNotifications) {
      if (pushWelcomeTimerRef.current) clearTimeout(pushWelcomeTimerRef.current);
      firstCrushPushTriggered.current = true;
      if (!showMatchPopupRef.current) setShowPushWelcome(true);
    }
  }, [user?.totalCrushesAdded, user?.pushNotifications]);



  useEffect(() => {
    let t: any;
    const reset = () => {
      clearTimeout(t);
      t = setTimeout(() => signOut().then(() => setStage("signin")), 600_000);
    };

    ["mousedown", "mousemove", "keypress", "scroll", "touchstart"].forEach((ev) =>
      document.addEventListener(ev, reset, true)
    );

    reset();

    return () => {
      clearTimeout(t);
      ["mousedown", "mousemove", "keypress", "scroll", "touchstart"].forEach((ev) =>
        document.removeEventListener(ev, reset, true)
      );
    };
  }, [setStage]);

const registerPushToken = async () => {
  void ['🔔 [Push] Starting registration...'];
  
  try {
    // Check platform support
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone =
      (window.navigator as any).standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;

    if (isIOS && !isStandalone) {
      void ['❌ [Push] iOS requires PWA'];
      throw new Error("IOS_PWA_REQUIRED");
    }

    if (!("serviceWorker" in navigator) || !("Notification" in window)) {
      void ['❌ [Push] Browser not supported'];
      throw new Error("UNSUPPORTED");
    }

    // Request permission
    void ['🔔 [Push] Requesting permission...'];
    const permission = await Notification.requestPermission();
    void ['🔔 [Push] Permission result:', permission];
    
    if (permission !== "granted") {
      void ['❌ [Push] Permission denied'];
      throw new Error("PERMISSION_DENIED");
    }

    // Get service worker and FCM token
    void ['🔔 [Push] Getting service worker...'];
    const registration = await navigator.serviceWorker.ready;
    void ['✅ [Push] Service worker ready'];
    
    void ['🔔 [Push] Getting FCM token...'];
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    
    if (!token) {
      void ['❌ [Push] No FCM token generated'];
      throw new Error("NO_TOKEN");
    }
    void ['✅ [Push] FCM token obtained:', token.substring(0, 20) + '...'];

    // Register with backend
    void ['🔔 [Push] Registering with backend...'];
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      void ['❌ [Push] No auth token'];
      throw new Error("NO_AUTH");
    }

    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/register-endpoint`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        token,
        platform: isIOS ? "ios" : "web",
        testPush: true,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      void ['❌ [Push] Backend registration failed:', errorText];
      throw new Error(`BACKEND_FAILED: ${response.status}`);
    }
    
  void ['✅ [Push] Backend registration successful'];

    // Persist the enabled flag to the user record
    if (user?.id) {
      const updated = await gqlUpdateUser({ id: user.id, pushNotifications: true });
      if (updated) setUser(prev => prev ? { ...prev, ...updated } : updated);
    }

    // Show Android battery saver tip
    const isAndroid = /android/i.test(navigator.userAgent);
    if (isAndroid) {
      toast.success(
        'Tip: To receive match alerts without delay, exclude Crushhh from Battery Saver in your phone settings.',
        { duration: 6000 }
      );
    }

    void ['✅ [Push] Registration complete!'];
    return true;

  } catch (error: any) {
    void ['❌ [Push] Registration failed:', error];
    throw error;
  }
};

  useEffect(() => {
    const handler = () => setShowPhotoFrame(true);
    window.addEventListener('crushhh:openPhotoBooth', handler);
    return () => window.removeEventListener('crushhh:openPhotoBooth', handler);
  }, []);

  useEffect(() => {
    (async () => {
      if (!cogUser?.sub) return;
      

      try {
        let u = await gqlGetUser(cogUser.sub);
        let isNewUser = false;
        if (!u) {
          u = await gqlCreateUser(
            cogUser.sub,
            (cogUser.email ?? "") as string,
            (cogUser.phone_number ?? "") as string
          );
          isNewUser = true;

          // gqlCreateUser no longer writes User.email — call the encrypted
          // writer Lambda for new users so the field is populated server-side.
          if (u && cogUser.email) {
            try {
              const newUserSession = await fetchAuthSession({ forceRefresh: true });
              const newUserIdToken = newUserSession.tokens?.idToken?.toString();
              if (newUserIdToken) {
                const rawBase = import.meta.env.VITE_API_BASE_URL as string;
                const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
                await updateUserEmail(apiBase, `Bearer ${newUserIdToken}`, cogUser.email as string);
              }
            } catch {
              // non-fatal — email write is retried via REST on next session
            }
          }
        }
        void isNewUser; // currently unused downstream; reserved for future bootstrap flags
        if (!u) return;

        // Fetch decrypted PII (email, phone, vaults) via REST. Those fields
        // are no longer returned in AppSync selection sets. Merge into `u`
        // before any downstream logic reads email/phone/vault items.
        try {
          const piiSession = await fetchAuthSession({ forceRefresh: true });
          const piiIdToken = piiSession.tokens?.idToken?.toString();
          if (piiIdToken) {
            const rawBase = import.meta.env.VITE_API_BASE_URL as string;
            const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
            const pii = await fetchUserPii(apiBase, `Bearer ${piiIdToken}`);
            u = {
              ...u,
              email: (pii?.email || (cogUser.email ?? "") || u.email) ?? null,
              phone: (pii?.phone || (cogUser.phone_number ?? "") || u.phone) ?? null,
              contactPointsVaultEnc: JSON.stringify({ items: pii?.contactPointsVault ?? [] }),
              crushListVaultEnc: JSON.stringify({ items: pii?.crushListVault ?? [] }),
            };
          }
        } catch (piiErr) {
          void ['[UserData] fetchUserPii failed:', piiErr];
        }

        if (u.email) {
          try {
            const upsertSession = await fetchAuthSession({ forceRefresh: true });
            const upsertIdToken = upsertSession.tokens?.idToken?.toString();
            if (upsertIdToken) {
              const rawBase = import.meta.env.VITE_API_BASE_URL as string;
              const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
              await upsertEmailLookup(apiBase, `Bearer ${upsertIdToken}`, u.email);
            }
          } catch {
            // non-fatal — lookup is best-effort
          }
        }

        const items = getCPList(u);
        const primaryEmailRaw = (u.email ?? "") as string;
        const primaryPhoneRaw = (u.phone ?? "") as string;

        const normEmail = primaryEmailRaw ? normalizeEmail(primaryEmailRaw) : "";
        const normPhone = primaryPhoneRaw ? normalizePhone(primaryPhoneRaw) : "";

        const needsEmail =
          normEmail &&
          !items.some(
            (it: any) =>
              it?.kind === "email" && normalizeEmail(String(it?.value || "")) === normEmail
          );

        const needsPhone =
          normPhone &&
          !items.some(
            (it: any) =>
              it?.kind === "phone" && normalizePhone(String(it?.value || "")) === normPhone
          );

        let finalUser = u;

        if (needsEmail || needsPhone) {
          // NOTE: prior code did a 3s sleep + gqlGetUser refetch here as
          // race-protection against concurrent vault writes. After the PII
          // selection-set removal, gqlGetUser no longer returns the vault,
          // so we use `items` — already authoritative via fetchUserPii above.
          let mergedItems = [...items];

          if (
            needsEmail &&
            !items.some(
              (it: any) =>
                it?.kind === "email" && normalizeEmail(String(it?.value || "")) === normEmail
            )
          ) {
            mergedItems.push({
              kind: "email",
              value: normEmail,
              status: "verified",
              addedAt: new Date().toISOString(),
            } as CPItem);
          }

          if (
            needsPhone &&
            !items.some(
              (it: any) =>
                it?.kind === "phone" && normalizePhone(String(it?.value || "")) === normPhone
            )
          ) {
            mergedItems.push({
              kind: "phone",
              value: normPhone,
              status: "verified",
              addedAt: new Date().toISOString(),
            } as CPItem);
          }

          // Vault write goes through the encrypted REST Lambda. mergedItems
          // is sent plaintext; the Lambda encrypts each item's PII fields.
          try {
            const bootstrapSession = await fetchAuthSession({ forceRefresh: true });
            const bootstrapIdToken = bootstrapSession.tokens?.idToken?.toString();
            if (bootstrapIdToken) {
              const rawBase = import.meta.env.VITE_API_BASE_URL as string;
              const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
              await updateContactPointsVault(apiBase, `Bearer ${bootstrapIdToken}`, mergedItems);
            }
          } catch {
            // non-fatal — vault write is retried on next session
          }

          // Reflect new vault locally on `u` (bootstrap doesn't go through
          // setUser yet — finalUser is the source of truth for the final set).
          finalUser = {
            ...u,
            contactPointsVaultEnc: writeJson({ items: mergedItems }),
          };
        }

        setUser(finalUser);
        setPushNotifications(!!finalUser.pushNotifications);
        setAccountActive(!!finalUser.accountActive);
        setCookiePolicyChoice(finalUser.cookiePolicyChoice || "strict");
      } catch (err) {
        console.error("[UserData] create/get user error:", err);
        
      }
    })();
  }, [cogUser]);

  useEffect(() => {
    if (!user || claimedPrimaryOnce.current) return;
    claimedPrimaryOnce.current = true;

    (async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) return;

        const call = async (kind: "phone" | "email", value: string) => {
          const endpoint = import.meta.env.VITE_ATTACH_ENDPOINT;
          if (!endpoint) return;
          const resp = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ kind, value }),
          });
          if (!resp.ok) {
            void ["attach-contact-point failed", await resp.text()];
          }
        };

        const email = (user.email ?? "").trim().toLowerCase();

        if (email) {
          await call("email", email);
        }
        // Phone ContactPoint is now created server-side by verifyContactPointOTP.
        // Auto-attach for phone was removed — attachContactPoint rejects phone with 410.

        // Replay pre-signup local lists into the authenticated account.
        // Order is fixed: handles first, then crushes. By the time addCrushProducer
        // fires per crush, all presignup handles are confirmed in ContactPoints and
        // visible in allConfirmed — so edge fan-out and reciprocal detection are
        // correct in a single pass. Runs once per session; each list resumes at the
        // first un-imported entry rather than replaying everything from scratch.
        try {
          const replaySession = await fetchAuthSession({ forceRefresh: true });
          const replayIdToken = replaySession.tokens?.idToken?.toString();
          if (replayIdToken) {
            const replayToken = `Bearer ${replayIdToken}`;
            const rawBase = import.meta.env.VITE_API_BASE_URL as string;
            const replayApiBase = rawBase.endsWith("/") ? rawBase : `${rawBase}/`;
            const attachEndpoint = import.meta.env.VITE_ATTACH_ENDPOINT as string;

            // 1. Handle conversion — attach + confirm each presignup handle.
            if (attachEndpoint) {
              await replayPresignupHandles(attachEndpoint, replayToken);
            }

            // 2. Crush replay — addCrushProducer sees confirmed handles in allConfirmed.
            const currentItems = getCrushList(user);
            await replayPresignupList(replayApiBase, replayToken, currentItems);
          }
        } catch {
          // non-fatal — replay is best-effort, retried on next login if localStorage not cleared
        }
      } catch (e) {
        void ["claimPrimaryIdsOnce error", e];
      }
    })();
  }, [user]);

  const prevMatchCountRef = useRef<number>(0);

  useEffect(() => {
    if (!user) return;

    const totalMatches = user.totalMatchesMade ?? 0;
    const totalSeen = user.totalMatchesSeen ?? 0;
    const unseenMatches = totalMatches - totalSeen;

    if (unseenMatches > 0 && user?.tier && user.tier !== 'free') {
      showMatchPopupRef.current = true; setShowPushWelcome(false); setShowMatchPopup(true);

      (async () => { 
        try { 
          await gqlUpdateUser({ 
            id: user.id, 
            totalMatchesSeen: totalMatches, 
          }); 
        
        } catch (e) { 
          void ["[MatchPopup] Failed to update totalMatchesSeen", e]; 
        } 
      })(); 
    } 

    prevMatchCountRef.current = totalMatches; 
  }, [user?.totalMatchesMade, user?.totalMatchesSeen]);

const saveContactPoints = async (items: CPItem[]) => {
  if (!user) return;
  // Vault write goes through the encrypted REST Lambda. `totalContactPoints`
  // is dropped — schema confirms it's analytics-only and not used for gating.
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      const rawBase = import.meta.env.VITE_API_BASE_URL as string;
      const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
      await updateContactPointsVault(apiBase, `Bearer ${idToken}`, items);
    }
  } catch {
    // non-fatal
  }
  // Reflect new vault locally so the cpItems memo re-runs without refetch.
  setUser(prev => prev ? {
    ...prev,
    contactPointsVaultEnc: writeJson({ items }),
  } : prev);
  setMatchesRefreshKey((prev) => prev + 1);
};


  const updateCrushVault = async (items: Crush[]) => {
    if (!user) return;
    // Vault write goes through the encrypted REST Lambda. The Lambda encrypts
    // each item's `contact` field server-side under `contactEnc`.
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        const rawBase = import.meta.env.VITE_API_BASE_URL as string;
        const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
        await updateCrushListVault(apiBase, `Bearer ${idToken}`, items);
      }
    } catch {
      // non-fatal
    }
    // Reflect new vault locally so the crushes memo re-runs without refetch.
    setUser(prev => prev ? {
      ...prev,
      crushListVaultEnc: writeJson({ items }),
    } : prev);
    setMatchesRefreshKey((prev) => prev + 1);
  };

  const handleDeleteCrush = async (contact: string) => {
    if (!user) return;

    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      const removeEndpoint = import.meta.env.VITE_REMOVE_CRUSH_ENDPOINT;

      if (idToken && removeEndpoint) {
        const verified = cpItems.filter((x: any) => {
          const s = (x?.status ?? "").toString().toLowerCase();
          return s === "active" || s === "verified" || s === "ok";
        });

        const initiatorCIDs = verified.map((v: any) => {
          const kind = v.kind === "phone" || v.kind === "email" ? v.kind : "handle";
          const payload: any = { kind };
          if (kind === "phone") payload.value = normalizePhone(v.value);
          else if (kind === "email") payload.value = normalizeEmail(v.value);
          else {
            payload.value = (v.value || "").trim();
            payload.platform = v.kind;
          }
          return payload;
        });

        const resp = await fetch(removeEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ target: contact.trim(), initiatorCIDs }),
        });

        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || json?.ok !== true) {
          void ["remove-crush failed", resp.status, json];
        }
      }
    } catch (e) {
      void ["remove-crush call failed", e];
    }

    const newList = crushes.filter((c) => c.contact !== contact);
    await updateCrushVault(newList);

    const updateInput: any = {
      id: user.id,
    };

    const updatedUser = await gqlUpdateUser(updateInput);
    if (updatedUser) {
      setUser(prev => prev ? { ...prev, ...updatedUser } : updatedUser);
    }
  };

  const handleEmailChangeSuccess = async (newEmail: string) => {
    try {
      if (!user) {
        toast.error("User data not loaded. Please refresh and try again.");
        return;
      }

      // User.email is no longer written through AppSync — both the encrypted
      // User.email write and the hash-keyed PrimaryEmailLookup write go
      // through dedicated REST Lambdas now.
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          const rawBase = import.meta.env.VITE_API_BASE_URL as string;
          const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
          await updateUserEmail(apiBase, `Bearer ${idToken}`, newEmail);
          await upsertEmailLookup(apiBase, `Bearer ${idToken}`, newEmail);
        }
      } catch {
        // non-fatal — both writes are best-effort
      }

      // Reflect the new email in local state immediately so settings UI shows
      // the updated value without waiting for the next bootstrap.
      setUser(prev => prev ? { ...prev, email: newEmail } : prev);

      setShowChangeEmail(false);
      toast.success("Primary email updated successfully!");
    } catch (error) {
      void ["Error updating email in database:", error];
      toast.error("Email updated in Cognito but failed to update database. Please refresh.");
    }
  };

  const handlePhoneChangeSuccess = async (newPhoneE164: string, oldPhone: string) => {
    try {
      if (!user) {
        toast.error("User data not loaded. Please refresh and try again.");
        return;
      }

      // verifyContactPointOTP has already written User.phone = newPhoneE164 and
      // User.phoneVerified = true server-side. Brief R's mirrorVaultItemByKindValue
      // also fired inside that Lambda, marking the (kind: 'phone') vault item as
      // verified — but kind-only match hits the *first* phone vault item, which
      // at this point is the old phone (its valueEnc is unchanged). That's fine
      // — the old phone is being demoted to a regular verified vault item, so
      // the verified/confirmedByUser flags on it should stay true.
      //
      // To make sure the old phone explicitly exists in the vault as a regular
      // ContactPoint (per Brief AA), append it as a new vault item if it's not
      // already represented by its plaintext value.
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          const rawBase = import.meta.env.VITE_API_BASE_URL as string;
          const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;

          // Build the new items array. cpItems is the decrypted vault from
          // getUserPii. Preserve every existing item; append old phone only
          // if it isn't already in the vault as a plaintext-matching entry.
          const existing = cpItems as any[];
          const oldPhoneAlreadyInVault = existing.some(
            (i: any) => i?.kind === "phone" && (i?.value || "") === oldPhone
          );

          const next = oldPhoneAlreadyInVault
            ? existing
            : [
                ...existing,
                {
                  kind: "phone",
                  value: oldPhone,
                  status: "verified",
                  confirmedByUser: true,
                  verified: true,
                  addedAt: new Date().toISOString(),
                },
              ];

          await updateContactPointsVault(apiBase, `Bearer ${idToken}`, next);

          // Pull fresh PII so cpItems reflects both the demoted old phone and
          // the new primary phone (auto-attach effect will add the new phone
          // separately on its own pass).
          await refreshUserPii(apiBase, `Bearer ${idToken}`, setUser);
        }
      } catch {
        // non-fatal — the primary change already succeeded server-side
      }

      // Reflect the new primary phone in local state immediately.
      setUser(prev => prev ? { ...prev, phone: newPhoneE164, phoneVerified: true } : prev);

      setShowChangePhone(false);
      toast.success("Primary phone updated successfully!");
    } catch (error) {
      void ["Error finalizing phone change:", error];
      toast.error("Phone updated but vault refresh failed. Please refresh the page.");
    }
  };

const handleAddCrushPending = async (contact: string, interest: Interest, contactType: string) => {
  if (!user) return;

  if (crushes.some((c) => c.contact === contact)) {
    toast.error(`"${contact}" is already on your Crushhh-List.`);
    return;
  }

  // ✅ DO NOT compute hash here! Let backend do it.
  const newItem: Crush = {
    contact,
    // contactHash: undefined,  ← Don't set it!
    interestLevel: interest,
    addedAt: new Date().toISOString(),
    contactType,
    status: "pending",
  };

  await updateCrushVault([...crushes, newItem]);
};

const handleConfirmCrush = async (contact: string): Promise<{ hasMatch?: boolean }> => {
  if (!user) return { hasMatch: false };

  const crush = crushes.find((c) => c.contact === contact && c.status === "pending");
  if (!crush) {
    toast.error("Crush not found or already confirmed.");
    return { hasMatch: false };
  }

  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();
    const endpoint = import.meta.env.VITE_ADD_CRUSH_ENDPOINT;

    if (!idToken || !endpoint) {
      toast.error("App not configured or session expired.");
      return { hasMatch: false };
    }

    const verified = cpItems.filter((x: any) => {
      const s = (x?.status ?? "").toString().toLowerCase();
      return s === "active" || s === "verified" || s === "ok";
    });

    const initiatorCIDs = verified
      .filter((v: any) => v.kind === "phone" || v.kind === "email")
      .map((v: any) => {
        const kind = v.kind as "phone" | "email";
        const payload: any = { kind };
        if (kind === "phone") payload.value = normalizePhone(v.value);
        else payload.value = normalizeEmail(v.value);
        return payload;
      });

    const payload: any = {
      target: contact,
      interest: crush.interestLevel,
      initiatorCIDs,
    };

    if (crush.contactType === 'phone' || crush.contactType === 'email') {
      payload.contactType = crush.contactType;
    } else if (crush.contactType) {
      payload.platform = crush.contactType;
    }

    const resp = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(payload),
    });

    const json = await resp.json().catch(() => ({}));

    if (!resp.ok || !json?.ok) {
      toast.error("Could not process your crush right now. Try again.");
      return { hasMatch: false };
    }
    if ((json.enqueued ?? 0) === 0) {
      toast.error("No usable IDs on file for matching. Toggle Use for matching on at least one Crushhh-ID.");
      return { hasMatch: false };
    }
  } catch (e) {
    void ["confirm-crush backend call failed", e];
    toast.error("Network error. Try again.");
    return { hasMatch: false };
  }

  // ✅ DON'T update vault - backend already did it with the correct hash!
  // Just refetch user data to get the updated vault from backend
  const matchCheckBefore = user.totalMatchesMade ?? 0;
  
  // Wait for backend to process match detection
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  try {
    const refreshedUser = await gqlGetUser(user.id);
    if (refreshedUser) {
      setUser(prev => prev ? { ...prev, ...refreshedUser } : refreshedUser);

      // Refresh PII (vault) — getUserPii is the authoritative read path for
      // crushListVaultEnc, which the just-completed add wrote to server-side.
      // Re-fetch the session here because `idToken` from the outer try is no
      // longer in scope at this point.
      try {
        const piiSession = await fetchAuthSession({ forceRefresh: true });
        const piiToken = piiSession.tokens?.idToken?.toString();
        if (piiToken) {
          const rawBase = import.meta.env.VITE_API_BASE_URL as string;
          const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
          await refreshUserPii(apiBase, `Bearer ${piiToken}`, setUser);
        }
      } catch {
        // non-fatal
      }

      const matchCheckAfter = refreshedUser.totalMatchesMade ?? 0;
      const hadMatch = matchCheckAfter > matchCheckBefore;

      return { hasMatch: hadMatch };
    }
  } catch (e) {
    void ["[UserData] Failed to refetch user after crush confirm", e];
  }

  return { hasMatch: false };
};

/**
 * Re-confirm a crush after upgrading to paid tier
 * Reuses the same add-crush endpoint
 */
/**
 * Re-confirm a crush after upgrading to paid tier
 * Reuses the same add-crush endpoint
 */
const handleReconfirmCrush = async (contact: string, interestLevel: Interest, contactType?: string): Promise<void> => {
  if (!user) return;

  try {
    void ['🔄 Reconfirming crush:', { contact, interestLevel, contactType }];
    
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString();
    const endpoint = import.meta.env.VITE_ADD_CRUSH_ENDPOINT;

    if (!idToken || !endpoint) {
      toast.error("App not configured or session expired.");
      return;
    }

    const verified = cpItems.filter((x: any) => {
      const s = (x?.status ?? "").toString().toLowerCase();
      return s === "active" || s === "verified" || s === "ok";
    });

    const initiatorCIDs = verified
      .filter((v: any) => v.kind === "phone" || v.kind === "email")
      .map((v: any) => {
        const kind = v.kind as "phone" | "email";
        const payload: any = { kind };
        if (kind === "phone") payload.value = normalizePhone(v.value);
        else payload.value = normalizeEmail(v.value);
        return payload;
      });

    const payload: any = {
      target: contact,
      interest: interestLevel,
      initiatorCIDs,
    };

    // ✅ Send contactType for phone/email, platform for social
    if (contactType === 'phone' || contactType === 'email') {
      payload.contactType = contactType;
    } else if (contactType) {
      payload.platform = contactType;
    }

    const resp = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(payload),
    });

    const json = await resp.json().catch(() => ({}));

    if (!resp.ok || !json?.ok) {
      toast.error("Could not process your crush right now. Try again.");
      return;
    }
    
    if ((json.enqueued ?? 0) === 0) {
      toast.error("No usable IDs on file for matching. Toggle Use for matching on at least one Crushhh-ID.");
      return;
    }

    // ✅ NEW: Clear the needsReconfirm flag in vault
    const updatedList = crushes.map(c =>
      c.contact === contact 
        ? { ...c, needsReconfirm: false }
        : c
    );
    await updateCrushVault(updatedList);

    // ✅ Mark as reconfirmed in state
    setReconfirmedCrushes(prev => new Set(prev).add(contact));
    
    void ['✅ Crush reconfirmed successfully'];

    // ✅ Wait for backend to process potential matches
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Refresh user data to check for new matches
    try {
      const refreshedUser = await gqlGetUser(user.id);
      if (refreshedUser) {
        setUser(prev => prev ? { ...prev, ...refreshedUser } : refreshedUser);
      }

      // Refresh PII (vault) — the reconfirm wrote to crushListVaultEnc.
      try {
        const rawBase = import.meta.env.VITE_API_BASE_URL as string;
        const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
        await refreshUserPii(apiBase, `Bearer ${idToken}`, setUser);
      } catch {
        // non-fatal
      }
    } catch (e) {
      void ["[UserData] Failed to refetch user after reconfirm", e];
    }
    
  } catch (e) {
    void ["❌ Reconfirm crush failed:", e];
    toast.error("Network error. Try again.");
  }
};

const handleAddCrushFromNoMatch = () => {
  setShowNoMatchModal(false);
  setExpandedSection("crushlist");
  setTimeout(() => {
    crushListSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => crushListRef.current?.focusAddInput(), 400);
  }, 300);
};

const handleNoMatchPostHint = () => {
  setShowNoMatchModal(false);
  window.dispatchEvent(new CustomEvent('crushhh:openPhotoBooth'));
};

const handleDeleteAccount = async (confirmationPhrase: string) => {
  const session = await fetchAuthSession({ forceRefresh: true });
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error('Session expired. Please refresh and try again.');
  const rawBase = import.meta.env.VITE_API_BASE_URL as string;
  const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
  const res = await fetch(`${apiBase}soft-delete-account`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ confirmationPhrase }),
  });
  const json = await res.json().catch(() => ({}));
  if (!json.ok) {
    if (json.reason === 'PHRASE_MISMATCH') throw new Error('Phrase did not match. Please try again.');
    throw new Error(json.reason || 'Deletion request failed. Please try again.');
  }
  // Sign out — the account is now locked and will be deleted in 7 days.
  await signOut();
  setStage("signin");
};

const saveSettings = async () => {
  if (!user) return;
  setLoading(true);
  
  try {
    const wasPushEnabled = user.pushNotifications;
    const isEnablingPush = pushNotifications && !wasPushEnabled;
    const isDisablingPush = !pushNotifications && wasPushEnabled;
    
    // ENABLE PUSH: Register token, then update user record
    if (isEnablingPush) {
      void ['🔔 [Settings] Enabling push notifications...'];
      try {
        await registerPushToken();
        
        // Update user record
        const updated = await gqlUpdateUser({
          id: user.id,
          pushNotifications: true,
          accountActive,
          cookiePolicyChoice,
        });
        
        if (updated) {
          setUser(prev => prev ? { ...prev, ...updated } : updated);
          setPushNotifications(true);
          void ['✅ [Settings] Push enabled successfully'];
          toast.success('Push notifications enabled! You\'ll receive match alerts.');
        }
        
      } catch (error: any) {
        void ['❌ [Settings] Push enable failed:', error];
        
        // Show user-friendly error
        if (error.message === "IOS_PWA_REQUIRED") {
          toast.error("On iOS, please add Crushhh to your home screen to enable notifications.");
        } else if (error.message === "UNSUPPORTED") {
          toast.error("Push notifications are not supported on this browser.");
        } else if (error.message === "PERMISSION_DENIED") {
          toast.error("Notification permission was denied. Please enable it in your browser settings.");
        } else if (error.message === "NO_TOKEN") {
          toast.error("Failed to generate notification token. Please refresh and try again.");
        } else {
          toast.error("Failed to enable push notifications. Please check your browser settings and try again.");
        }
        
        // Reset toggle since it failed
        setPushNotifications(false);
      } finally {
        setLoading(false);
        return; // Don't fall through to general update
      }
    }
    
    // DISABLE PUSH: Just update user record (keep token registered for now)
    if (isDisablingPush) {
      void ['🔕 [Settings] Disabling push notifications...'];
    }
    
    // Update settings (handles disable push or non-push changes)
    const updated = await gqlUpdateUser({
      id: user.id,
      pushNotifications,
      accountActive,
      cookiePolicyChoice,
    });
    
    if (updated) {
      setUser(prev => prev ? { ...prev, ...updated } : updated);
      void ['✅ [Settings] Settings saved'];
      
      if (isDisablingPush) {
        toast.success('Push notifications disabled. You can re-enable them anytime.');
      } else {
        toast.success('Settings saved successfully!');
      }
    }
    
  } catch (e) {
    void ["❌ [Settings] Save failed:", e];
    toast.error("Failed to save settings. Please try again.");
  } finally {
    setLoading(false);
  }
};

  const toMillis = (dt: AWSDateTimeLike): number | undefined => {
    if (dt == null) return undefined;
    if (typeof dt === "number") return dt * 1000;
    const ms = Date.parse(dt);
    return Number.isNaN(ms) ? undefined : ms;
  };


  const verifiedSocialLinks = useMemo(() => {
    const socials = ["instagram", "tiktok", "snapchat", "facebook", "linkedin"];
    const okStatuses = new Set(["active", "verified", "ok"]);

    const out: Partial<Record<string, string>> = {};
    for (const it of cpItems as any[]) {
      const kind = String(it?.kind || "").toLowerCase();
      if (!socials.includes(kind)) continue;

      const status = String(it?.status || "").toLowerCase();
      if (status && !okStatuses.has(status)) continue;

      const url = toSocialProfileUrl(kind, String(it?.value || ""));
      if (url) out[kind] = url;
    }
    return out;
  }, [cpItems]);

  const memberSince = useMemo(() => {
    if (!user?.createdAt) return null;
    return toMillis(user.createdAt);
  }, [user]);

  if (!user) {
    return (
      <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
        <div className="bg-transparent rounded-lg p-8 m-4 ">
          {errorMessage ? (
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#FFFFFF",
                background: "rgba(196, 75, 46, 0.92)",
                textAlign: "center",
                padding: "16px 20px",
                borderRadius: 12,
                maxWidth: 320,
                margin: 0,
              }}
            >
              {errorMessage}
            </p>
          ) : (
            <div className="flex items-center gap-3">
              <div
                className="animate-spin rounded-full h-80 w-80 zodiyak-spinner"
                style={{ animationDirection: 'reverse' }}
                aria-hidden="true"
              />
              {/*<p className="text-2xl font-medium text-gray-800">Signing you in...</p>*/}
            </div>
          )}
        </div>
      </div>
    );
  }

// Scheduled-for-deletion gate: accountActive=false AND deletedAt set means the
// user initiated deletion (not just toggled "Account Active" off). Show the
// grace-period screen so they can cancel. Bypasses all other gates — this
// takes priority over phone/age/payment gates for deleted accounts.
if (user.deletedAt) {
    async function callCancelDeletion() {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) throw new Error('Session expired. Please refresh and try again.');
      const rawBase = import.meta.env.VITE_API_BASE_URL as string;
      const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
      const res = await fetch(`${apiBase}cancel-account-deletion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!json.ok) throw new Error(json.reason || 'Cancellation failed.');
    }
    return (
      <ScheduledDeletionScreen
        deletedAt={user.deletedAt as string}
        onCancelDeletion={callCancelDeletion}
        onCancelSuccess={() => {
          setUser(prev => prev ? { ...prev, accountActive: true, deletedAt: null } : prev);
          setAccountActive(true);
        }}
      />
    );
  }

if (!user.ageVerified) {
    return (
      <AgeVerificationGate
        onComplete={async () => {
          window.scrollTo(0, 0);
          // Lambda already set ageVerified=true, zodiacSign, zipcode server-side — refetch authoritative value.
          // birthdate is no longer stored or written by either side.
          const refreshed = await gqlGetUser(user.id);
          if (refreshed) setUser(prev => prev ? { ...prev, ...refreshed } : refreshed);

          // Refresh PII state too — keep the gate-completion pattern uniform
          // across phone / age / payment so any post-gate PII drift converges.
          try {
            const session = await fetchAuthSession({ forceRefresh: true });
            const idToken = session.tokens?.idToken?.toString();
            if (idToken) {
              const rawBase = import.meta.env.VITE_API_BASE_URL as string;
              const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;
              await refreshUserPii(apiBase, `Bearer ${idToken}`, setUser);
            }
          } catch {
            // non-fatal
          }
        }}
      />
    );
  }

  if (!user.phoneVerified) {
    return (
      <PhoneVerificationGate
        onComplete={async () => {
          // Fetch the refreshed user record and decrypted vault in parallel
          // before calling setUser, so cpItems is correct on the first
          // crush-list render. Mirrors the [cogUser] bootstrap's single
          // setUser(finalUser) pattern — phoneVerified and vault land together.
          const session = await fetchAuthSession({ forceRefresh: true });
          const idToken = session.tokens?.idToken?.toString();
          const rawBase = import.meta.env.VITE_API_BASE_URL as string;
          const apiBase = rawBase.endsWith('/') ? rawBase : `${rawBase}/`;

          const [refreshed, pii] = await Promise.all([
            gqlGetUser(user.id),
            idToken
              ? fetchUserPii(apiBase, `Bearer ${idToken}`)
              : Promise.resolve(null),
          ]);

          setUser(prev => {
            if (!prev) return refreshed ?? prev;
            const base = refreshed ? { ...prev, ...refreshed } : { ...prev };
            if (!pii) return base;
            return {
              ...base,
              email: pii.email || base.email,
              phone: pii.phone || base.phone,
              contactPointsVaultEnc: JSON.stringify({ items: pii.contactPointsVault ?? [] }),
              crushListVaultEnc: JSON.stringify({ items: pii.crushListVault ?? [] }),
            };
          });
        }}
      />
    );
  }

  const zodiacSign: ZodiacSign = (user?.zodiacSign as ZodiacSign | null | undefined) ?? "Aquarius";

  if (upgradeStage === "plan" && user) {
    return (
      <PaymentGate
        userId={user.id}
        zodiacSign={zodiacSign}
        onCancel={() => setUpgradeStage("none")}
        onComplete={async (scrollToCrushes) => {
          const refreshed = await gqlGetUser(user.id);
          if (refreshed) setUser(prev => prev ? { ...prev, ...refreshed } : refreshed);
          setUpgradeStage("none");

          if (scrollToCrushes) {
            setTimeout(() => {
              setExpandedSection("crushlist");
              crushListSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              setTimeout(() => crushListRef.current?.focusAddInput(), 400);
            }, 300);
          } else {
            setTimeout(() => {
              setExpandedSection("crushids");
              crushIdsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
          }
        }}
      />
    );
  }


  return (
<div
  style={{
    background: BRAND_CREAM,
    minHeight: "100vh",
    padding: "16px 12px 80px",
    fontFamily: BRAND_FONT_BODY,
  }}
>
  <div
    style={{
      maxWidth: 640,
      margin: "0 auto",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    }}
  >
    {/* INACTIVE ACCOUNT BANNER */}
    {!accountActive && (
      <div className="bg-red-50 border-4 border-red-500 rounded-xl p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-red-900 mb-1">
              Account Inactive
            </h3>
            <p className="text-sm text-red-800 mb-3">
              Your profile is hidden. You won't receive notifications or show up in matches.
            </p>
            <button
              onClick={() => {
                setExpandedSection("settings");
                setTimeout(() => {
                  settingsSectionRef.current?.scrollIntoView({ 
                    behavior: "smooth", 
                    block: "center" 
                  });
                }, 100);
              }}
              className="text-sm font-semibold text-red-900 underline hover:text-red-700"
            >
              → Go to Settings to reactivate
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Secret Admirer Badge - participation gate based on totalCrushesAdded */}
    <SecretAdmirerBadge
      userId={user.id}
      isPaid={isPaid}
      onUpgrade={handleStartUpgrade}
      onTallyLoaded={setAdmirerCount}
      totalCrushesAdded={user.totalCrushesAdded}
      onAddCrush={() => {
        setExpandedSection("crushlist");
        setTimeout(() => {
          crushListSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      }}
    />

    {/* Meme Submission CTA - Show if eligible */}
    {admirerCount === 0 && (
      canSubmitToday ? (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 4, marginBottom: 8 }}>
          <button
            onClick={() => setShowMemeModal(true)}
            onMouseEnter={() => setPillHov('memeHint')}
            onMouseLeave={() => { setPillHov(null); setPillPress(null); }}
            onMouseDown={() => setPillPress('memeHint')}
            onMouseUp={() => setPillPress(null)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: pillPress === 'memeHint' ? "#B34428" : pillHov === 'memeHint' ? "#C94E30" : "#E05A3A",
              color: "#FFFFFF",
              border: "3px solid #1A1A1A",
              borderRadius: 100,
              boxShadow: "4px 4px 0px #1A1A1A",
              padding: "16px 32px",
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 20,
              fontWeight: 700,
              cursor: "pointer",
              width: "100%",
              maxWidth: 320,
              boxSizing: "border-box",
              transform: pillPress === 'memeHint' ? "scale(0.97)" : "scale(1)",
              transition: "all 0.1s ease",
            }}
          >
            Got a Good Crushhh Hint
          </button>
        </div>
      ) : (
        <div className="meme-cta-placeholder">
          Check back tomorrow for another chance!
        </div>
      )
    )}

        {expandedSection === "crushlist" && (
          <ZodiyakHelper
            zodiacSign={zodiacSign}
            currentSection={currentTipSection}
            isEnabled={zodiyakEnabled}
          />
        )}

        {/* MATCHES SECTION - Only show if user has matches */}
        {isPaid && (user?.totalMatchesMade ?? 0) > 0 && (
          <section
            style={{ ...sectionCardStyle, border: "3px solid #e54c4c", boxShadow: "4px 4px 0px #e54c4c" }}
          >
            <button
              onClick={(e) => {
                const next = expandedSection === "matches" ? null : "matches";
                setExpandedSection(next);
                setCurrentTipSection("matches");
                e.currentTarget.blur();
              }}
              style={sectionHeaderStyle(expandedSection === "matches")}
            >
              <img
                src={asset(`/matches.svg`)}
                alt="Matches"
                className="object-contain cursor-pointer h-8"
              />
              <svg
                className={`w-6 h-6 transition-transform duration-300 ${
                  expandedSection === "matches" ? "rotate-0" : "rotate-90"
                }`}
                style={{ color: BRAND_INK }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSection === "matches" && (
              <div className="mt-4" style={{ padding: "16px 20px" }}>
                <MatchesList
                  key={matchesRefreshKey}
                  crushList={crushes}
                  userVault={userVaultData}
                />
              </div>
            )}
          </section>
        )}

        {/* CRUSHLIST SECTION */}
        <section
          ref={crushListSectionRef}
          style={{
            ...sectionCardStyle,
            opacity: accountActive ? 1 : 0.4,
            pointerEvents: accountActive ? "auto" : "none",
            transition: "opacity 0.2s ease",
          }}
        >
          <button
            onClick={(e) => {
              const next = expandedSection === "crushlist" ? null : "crushlist";
              setExpandedSection(next);
              setCurrentTipSection("crushlist");
              e.currentTarget.blur();

              if (next === "crushlist") {
                requestAnimationFrame(() => {
                  const el = crushListSectionRef.current;
                  if (!el) return;

                  const headerOffset = 70;
                  const rect = el.getBoundingClientRect();
                  const offsetTop = window.scrollY + rect.top - headerOffset;

                  window.scrollTo({ top: offsetTop, behavior: "smooth" });
                });
              }
            }}
            style={sectionHeaderStyle(expandedSection === "crushlist")}
          >
            <img
              src={asset(`/crushhhlist.svg`)}
              alt="Crushhh-List"
              className="object-contain cursor-pointer h-8"
            />
            <svg
              className={`w-6 h-6 transition-transform duration-300 ${
                expandedSection === "crushlist" ? "rotate-0" : "rotate-90"
              }`}
              style={{ color: BRAND_INK }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSection === "crushlist" && (
            <div style={sectionBodyStyle}>
              <CrushList
                ref={crushListRef}
                items={crushes}
                onDelete={handleDeleteCrush}
                onAdd={handleAddCrushPending}
                onConfirm={handleConfirmCrush}
                tier={user?.tier ?? undefined}
                userId={user?.id}
                userZodiacSign={zodiacSign}
                dmLinks={verifiedSocialLinks}
                reconfirmedCrushes={reconfirmedCrushes}
                onReconfirm={handleReconfirmCrush}
                onNoMatch={() => setShowNoMatchModal(true)}
              />

              <div
                style={{
                  display: "inline-flex",
                  flexShrink: 0,
                  marginTop: 16,
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  border: "2px solid #1A1A1A",
                  borderRadius: 100,
                  boxShadow: "3px 3px 0px #1A1A1A",
                  padding: "4px 14px",
                  background: "#F0DFC8",
                  color: "#1A1A1A",
                }}
                title="Crushhh-List Slot Credits : Active Crushhh Entries Used / Max Active Crushhh-List Slots"
              >
                <span>{leftCrushes} Left</span>
                <span style={{ width: 1, height: 12, background: "rgba(26,26,26,0.4)" }} aria-hidden />
                <span>
                  {usedCreditsDisplay}/{crushCap} Used
                </span>
              </div>
            </div>
          )}
        </section>

        {expandedSection === "crushids" && (
          <ZodiyakHelper
            zodiacSign={zodiacSign}
            currentSection={currentTipSection}
            isEnabled={zodiyakEnabled}
          />
        )}

        <section
          ref={crushIdsSectionRef}
          style={{
            ...sectionCardStyle,
            opacity: accountActive ? 1 : 0.4,
            pointerEvents: accountActive ? "auto" : "none",
            transition: "opacity 0.2s ease",
          }}
        >
          <button
            onClick={(e) => {
              const next = expandedSection === "crushids" ? null : "crushids";
              setExpandedSection(next);
              setCurrentTipSection("crushids");
              e.currentTarget.blur();

              if (next === "crushids") {
                requestAnimationFrame(() => {
                  const el = crushIdsSectionRef.current;
                  if (!el) return;

                  const headerOffset = 70;
                  const rect = el.getBoundingClientRect();
                  const offsetTop = window.scrollY + rect.top - headerOffset;

                  window.scrollTo({ top: offsetTop, behavior: "smooth" });
                });
              }
            }}
            style={sectionHeaderStyle(expandedSection === "crushids")}
          >
            <img
              src={asset(`/crushhhids.svg`)}
              alt="Crushhh-IDs"
              className="object-contain cursor-pointer h-8"
            />
            <svg
              className={`w-6 h-6 transition-transform duration-300 ${
                expandedSection === "crushids" ? "rotate-0" : "rotate-90"
              }`}
              style={{ color: BRAND_INK }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSection === "crushids" && (
            <div style={sectionBodyStyle}>
              <ContactPointsPanel
                key={`cp-${user.id}-${String(user.contactPointsVaultEnc ?? "").length}-${String(user.updatedAt ?? "")}`}
                initialItems={cpItems}
                primaryEmail={user.email ?? ""}
                primaryPhone={user.phone ?? ""}
                onSave={saveContactPoints}
                dmLinks={DM_LINKS}
                userTier={user.tier ?? 'free'}
                setUser={setUser}
              />
            </div>
          )}
        </section>

        <section
          ref={settingsSectionRef}
          style={sectionCardStyle}
        >
          <button
            onClick={(e) => {
              const next = expandedSection === "settings" ? null : "settings";
              setExpandedSection(next);
              setCurrentTipSection("settings");
              e.currentTarget.blur();

              if (next === "settings") {
                requestAnimationFrame(() => {
                  const el = settingsSectionRef.current;
                  if (!el) return;

                  const headerOffset = 70;
                  const rect = el.getBoundingClientRect();
                  const offsetTop = window.scrollY + rect.top - headerOffset;

                  window.scrollTo({ top: offsetTop, behavior: "smooth" });
                });
              }
            }}
            style={sectionHeaderStyle(expandedSection === "settings")}
          >
            <img
              src={asset(`/usersettings.svg`)}
              alt="User Settings"
              className="object-contain cursor-pointer h-8"
            />
            <svg
              className={`w-6 h-6 transition-transform duration-300 ${
                expandedSection === "settings" ? "rotate-0" : "rotate-90"
              }`}
              style={{ color: BRAND_INK }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedSection === "settings" && (
            <div style={{ ...sectionBodyStyle, display: "flex", flexDirection: "column", gap: 16 }}>
              <ZodiyakHelper
                zodiacSign={zodiacSign}
                currentSection={currentTipSection}
                isEnabled={zodiyakEnabled}
              />

                {isPaid ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 4 }}>
                    <p
                      style={{
                        fontFamily: BRAND_FONT_DISPLAY,
                        fontSize: 16,
                        fontWeight: 700,
                        color: BRAND_CORAL,
                        margin: 0,
                      }}
                    >
                      Current Membership: Paid
                    </p>
                    {memberSince && (
                      <p style={{ ...fieldValueStyle, marginTop: 0 }}>
                        Member Since: {new Date(memberSince).toLocaleDateString()}
                      </p>
                    )}
                    {(() => {
                      const isLifetime = !user.planExpiresAt ||
                        new Date(user.planExpiresAt).getFullYear() >= 2099;
                      if (isLifetime) {
                        return (
                          <p style={{ ...fieldValueStyle, marginTop: 0 }}>
                            Lifetime membership ✓
                          </p>
                        );
                      }
                      const ms = toMillis(user.planExpiresAt ?? null);
                      return ms ? (
                        <p style={{ ...fieldValueStyle, marginTop: 0 }}>
                          Expires: {new Date(ms).toLocaleDateString()}
                        </p>
                      ) : null;
                    })()}
                  </div>
                ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button
                    onClick={(e) => {
                      setMembershipExpanded(!membershipExpanded);
                      e.currentTarget.blur();
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "space-between",
                      paddingRight: 40,
                      textAlign: "left",
                      paddingTop: 4,
                      paddingBottom: 4,
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: BRAND_FONT_DISPLAY,
                        fontSize: 16,
                        fontWeight: 700,
                        color: BRAND_CORAL,
                      }}
                    >
                      Current Membership: ( Free )
                    </span>
                    <span style={{ fontSize: 18, color: BRAND_INK }}>
                      {membershipExpanded ? "▼" : "▶"}
                    </span>
                  </button>

                  {membershipExpanded && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 4 }}>
                      <p
                        style={{
                          fontFamily: BRAND_FONT_DISPLAY,
                          fontSize: 15,
                          fontWeight: 700,
                          color: BRAND_INK,
                          margin: 0,
                        }}
                      >
                        Membership & Upgrades
                      </p>
                      <p style={{ ...fieldValueStyle, marginTop: 0 }}>
                        Add up to <span style={{ fontWeight: 700 }}>12</span> people to your Crushhh-List.<br/>
                        After 12 you have 1 update per day.
                      </p>
                      <p style={{ ...fieldValueStyle, marginTop: 0 }}>
                        Unlock lifetime matching for only $7.95.<br/>
                        No Fees. No Auto-Renewal. No Subscriptions.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div style={dividerRowStyle}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={fieldLabelStyle}>Primary Email</p>
                    <p style={fieldValueStyle}>{user.email ?? "Not set"}</p>
                  </div>
                  <button
                    onClick={() => setShowChangeEmail(true)}
                    onMouseEnter={() => setPillHov('changeEmail')}
                    onMouseLeave={() => { setPillHov(null); setPillPress(null); }}
                    onMouseDown={() => setPillPress('changeEmail')}
                    onMouseUp={() => setPillPress(null)}
                    style={{ ...actionPillStyle, background: pillPress === 'changeEmail' ? "#B34428" : pillHov === 'changeEmail' ? "#C94E30" : BRAND_CORAL, transform: pillPress === 'changeEmail' ? "scale(0.97)" : "scale(1)" }}
                  >
                    Change Email
                  </button>
                </div>
              </div>

              <div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={fieldLabelStyle}>Primary Phone</p>
                    <p style={fieldValueStyle}>{user.phone ? displayPhone(user.phone) : "Not set"}</p>
                  </div>
                  <button
                    onClick={() => setShowChangePhone(true)}
                    onMouseEnter={() => setPillHov('changePhone')}
                    onMouseLeave={() => { setPillHov(null); setPillPress(null); }}
                    onMouseDown={() => setPillPress('changePhone')}
                    onMouseUp={() => setPillPress(null)}
                    style={{ ...actionPillStyle, background: pillPress === 'changePhone' ? "#B34428" : pillHov === 'changePhone' ? "#C94E30" : BRAND_CORAL, transform: pillPress === 'changePhone' ? "scale(0.97)" : "scale(1)" }}
                  >
                    Change Phone
                  </button>
                </div>
              </div>

              <div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={fieldLabelStyle}>Password Settings</p>
                    <p style={fieldValueStyle}>***********</p>
                  </div>
                  <button
                    onClick={() => setStage("forgot-password")}
                    onMouseEnter={() => setPillHov('resetPassword')}
                    onMouseLeave={() => { setPillHov(null); setPillPress(null); }}
                    onMouseDown={() => setPillPress('resetPassword')}
                    onMouseUp={() => setPillPress(null)}
                    style={{ ...actionPillStyle, background: pillPress === 'resetPassword' ? "#B34428" : pillHov === 'resetPassword' ? "#C94E30" : BRAND_CORAL, transform: pillPress === 'resetPassword' ? "scale(0.97)" : "scale(1)" }}
                  >
                    Reset Password
                  </button>
                </div>
              </div>


              <div style={dividerRowStyle}>
                <ProfileSettings
                  pushNotifications={pushNotifications}
                  setPushNotifications={setPushNotifications}
                  accountActive={accountActive}
                  setAccountActive={setAccountActive}
                  cookiePolicyChoice={cookiePolicyChoice}
                  setCookiePolicyChoice={setCookiePolicyChoice}
                  isPushNotificationOpen={isPushNotificationOpen}
                  setisPushNotificationOpen={setisPushNotificationOpen}
                  isAccountActivationOpen={isAccountActivationOpen}
                  setisAccountActivationOpen={setisAccountActivationOpen}
                  isCookiesOpen={isCookiesOpen}
                  setCookiesOpen={setCookiesOpen}
                  onDeleteAccountClick={() => setIsDeleteModalOpen(true)}
                />
              </div>
            </div>
          )}
        </section>

        <DeleteAccountModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteAccount}
        />

        {showMatchPopup && <MatchPopup onClose={() => { showMatchPopupRef.current = false; setShowMatchPopup(false); }} suppressNotifications={true} />}

        {showNoMatchModal && (
          <WaitWithHopeModal
            mode="popup"
            isOpen={showNoMatchModal}
            onClose={() => setShowNoMatchModal(false)}
            onAddCrush={handleAddCrushFromNoMatch}
            onPostHint={handleNoMatchPostHint}
          />
        )}

        <section
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 12,
            marginTop: 8,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={saveSettings}
            disabled={loading}
            onMouseEnter={() => !loading && setPillHov('saveSettings')}
            onMouseLeave={() => { setPillHov(null); setPillPress(null); }}
            onMouseDown={() => !loading && setPillPress('saveSettings')}
            onMouseUp={() => setPillPress(null)}
            style={{
              ...footerPillCoralStyle,
              background: (!loading && pillPress === 'saveSettings') ? "#B34428" : (!loading && pillHov === 'saveSettings') ? "#C94E30" : BRAND_CORAL,
              transform: (!loading && pillPress === 'saveSettings') ? "scale(0.97)" : "scale(1)",
              opacity: loading ? 0.65 : 1,
              cursor: loading ? "default" : "pointer",
            }}
          >
            {loading ? "Saving..." : "Save Settings"}
          </button>
          <button
            onClick={async () => {
              await signOut();
              setStage("signin");
            }}
            onMouseEnter={() => setPillHov('signOut')}
            onMouseLeave={() => { setPillHov(null); setPillPress(null); }}
            onMouseDown={() => setPillPress('signOut')}
            onMouseUp={() => setPillPress(null)}
            style={{
              ...footerPillGhostStyle,
              background: pillPress === 'signOut' ? "rgba(0,0,0,0.12)" : pillHov === 'signOut' ? "rgba(0,0,0,0.06)" : "#FFFFFF",
              transform: pillPress === 'signOut' ? "scale(0.97)" : "scale(1)",
            }}
          >
            Sign Out
          </button>
        </section>

        {showChangeEmail && (
          <ChangePrimaryEmail
            currentEmail={user.email ?? ""}
            onSuccess={handleEmailChangeSuccess}
            onCancel={() => setShowChangeEmail(false)}
          />
        )}

        {showChangePhone && (
          <ChangePrimaryPhone
            currentPhone={user.phone ?? ""}
            onSuccess={handlePhoneChangeSuccess}
            onCancel={() => setShowChangePhone(false)}
          />
        )}

        <PushNotificationWelcomeModal
          isOpen={showPushWelcome}
          onClose={() => setShowPushWelcome(false)}
          zodiacSign={zodiacSign}
        onEnablePush={async () => {
          try {
            await registerPushToken();
          } catch (error: any) {
            console.error('[Push] registerPushToken failed:', error);
            // Don't return — still persist the user's intent below
          }
          // Always update AppSync regardless of FCM success/failure
          try {
            if (user?.id) {
              const updated = await gqlUpdateUser({ id: user.id, pushNotifications: true });
              if (updated) setUser(prev => prev ? { ...prev, ...updated } : updated);
              setPushNotifications(true);
            }
          } catch (updateError: any) {
            console.error('[Push] gqlUpdateUser failed:', updateError);
          }
        }}
          onDismiss={async () => {
            if (user) {
              await gqlUpdateUser({
                id: user.id,
                pushNotificationReminderDismissed: true,
              });
            }
          }}
        />
      </div>
            <MemeSubmissionModal
        isOpen={showMemeModal}
        onClose={() => {
          setShowMemeModal(false);
          checkDailySubmission(); // Refresh eligibility
        }}
      />
      {showPhotoFrame && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: '#E8D4B8',
          overflowY: 'auto',
        }}>
          <ZodiacPhotoFrame
            selectedSign={user?.zodiacSign as any}
            onShareComplete={() => setShowPhotoFrame(false)}
            onDismiss={() => setShowPhotoFrame(false)}
          />
        </div>
      )}
    </div>
  );
}